import { type Theme, styled, useTheme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { type MovieStatus, type TvStatus, type VideoType } from '@tmdb-graphql-api/schema/types';
import { type JSX } from 'react';
import { Query as CertificationIconQuery } from '#components/CertificationIcon/index.ts';
import { MoreOptionButton } from '#components/MoreOptionsButton/index.tsx';
import { CertificationIconContainer } from '#components/PosterImage/styled.ts';
import { Ribbon } from '#components/Ribbon/index.tsx';
import { VideoButton } from '#components/VideoButton/index.tsx';
import { type SectionType } from '#enums/mediaTypes.ts';
import { mapSectionTypeToScreenFormatType, mapSectionTypeToScreenType } from '#helpers/enumMappers.ts';
import { isMovieOrTv } from '#helpers/isMovieOrTv.ts';
import { isMovieTvOrSeason } from '#helpers/isMovieTvOrSeason.ts';
import { isPerson } from '#helpers/isPerson.ts';
import { type ActivePreview, type ActiveVideo, type PathIds } from '#types.ts';

export const OuterContainer = styled('div')({
  position: 'relative',
});

export const Container = styled('div')(({ theme }) => ({
  borderRadius: theme.spacing(1),
  height: 0,
  overflow: 'hidden',
  paddingBottom: '56%',
}));

export const Image = styled('img')(({ theme }) => ({
  borderRadius: theme.spacing(1),
  display: 'block',
  objectFit: 'cover',
  objectPosition: 'center',
  width: '100%',
}));

export type BackdropImpageProps = {
  action?: 'preview' | 'video';
  certificationSx?: SystemStyleObject<Theme>;
  id?: string | number;
  pathIds?: PathIds;
  pictureContainerSx?: SystemStyleObject<Theme>;
  renderAdditionalOptionItems?: () => JSX.Element | undefined;
  renderSlot?: () => JSX.Element | undefined;
  ribbonSx?: SystemStyleObject<Theme>;
  setActivePreview?: (item: ActivePreview) => void;
  setActiveVideo?: (item: ActiveVideo) => void;
  showCertification?: boolean;
  showMoreOptions?: boolean;
  showRibbon?: boolean;
  src: string;
  status?: MovieStatus | TvStatus;
  sx?: SystemStyleObject<Theme>;
  title: string;
  type: SectionType;
  videoButtonSx?: SystemStyleObject<Theme>;
  videoComponent?: string;
  videoCount?: number;
  videoTypes?: VideoType[];
};

export const BackdropImage = ({
  action,
  certificationSx,
  id,
  pathIds,
  pictureContainerSx,
  renderAdditionalOptionItems,
  renderSlot,
  ribbonSx,
  setActivePreview,
  setActiveVideo,
  showCertification = false,
  showMoreOptions,
  showRibbon = false,
  src,
  status,
  sx,
  title,
  type,
  videoButtonSx,
  videoComponent,
  videoCount,
  videoTypes,
}: BackdropImpageProps) => {
  const theme = useTheme();

  return (
    <OuterContainer sx={sx}>
      <div style={{ position: 'relative' }}>
        <Container sx={pictureContainerSx}>
          <picture>
            <Image alt={title} loading="lazy" src={src} />
          </picture>
          {isMovieOrTv(type) && showCertification && id ? (
            <CertificationIconQuery id={id} type={mapSectionTypeToScreenType(type)}>
              {certificationIcon => (
                <CertificationIconContainer sx={certificationSx}>{certificationIcon}</CertificationIconContainer>
              )}
            </CertificationIconQuery>
          ) : undefined}
        </Container>
      </div>
      {renderSlot?.()}
      {isMovieTvOrSeason(type) && showRibbon && id ? (
        <Ribbon id={id} status={status} sx={ribbonSx} type={mapSectionTypeToScreenFormatType(type)} />
      ) : undefined}
      {isMovieOrTv(type) && showMoreOptions && status ? (
        <MoreOptionButton
          dropShadow
          // @ts-expect-error move and tv ids are always numbers
          id={id}
          renderAdditionalOptionItems={renderAdditionalOptionItems}
          status={status}
          sx={{
            color: theme.mainTextSecondary,
            position: 'absolute',
            right: theme.spacing(0.5),
            top: theme.spacing(0.5),
          }}
          type={mapSectionTypeToScreenType(type)}
        />
      ) : undefined}
      {!isPerson(type) && setActiveVideo && id ? (
        <VideoButton
          action={action}
          component={videoComponent}
          id={id}
          pathIds={pathIds}
          setActivePreview={setActivePreview}
          setActiveVideo={setActiveVideo}
          sx={videoButtonSx}
          title={title}
          type={mapSectionTypeToScreenFormatType(type)}
          videoCount={videoCount}
          videoTypes={videoTypes}
        />
      ) : undefined}
    </OuterContainer>
  );
};
